// Typed kiosk endpoint functions. Backed by GET /api/v1/kiosks/*.
//
// CHG-2061 / ITER-4159 (kiosk-γ.1) — dashboard-v2 port of the v1
// kiosksApi at web/dashboard/src/api.ts:1148-1170. Consumed by the
// hand-authored config at apps/dashboard-v2/src/generated/screens/
// kiosk-list.ts (no entity-hint exists for kiosk yet; the config is
// hand-rolled but follows the same override-block layout as the
// generator emits for canary entities so a future v2gen run lands
// cleanly). Spec: specs/tier-04-interface/kiosk-management.api.yaml.
//
// Envelope normalization: the kiosk-management BE uses the older
// {data, meta:{page,per_page,total,pages}} envelope (1-indexed page,
// per_page). The v2 ListPage queryFn signature expects {items, total}
// with a 0-indexed page + pageSize, so listKiosks() translates the
// inbound page (0-based) → page (1-based) before the request and
// translates the response envelope back to {items, total} for the
// page primitive. Mirrors the v1 normalizeListResponse helper.
//
// CHG-1198 / ITER-3317 — Tap-to-Pay (Soft) vs M2 (Hardware) `reader_type`
// surfaces in the list column via the kiosk-list config. The settings
// embed (kiosk_mode preset_amounts / default_designation / receipt_prompt /
// exit_pin_hash) ships in γ.2 (KioskDetail).

import { apiClient } from "./client";

// ── Value-space unions ─────────────────────────────────────────────
// Mirror tier-04 KioskRead.status + reader_status + reader_type
// description columns. Wider than v1's string literal types so an
// unrecognized BE value falls through to display, not break.

export type KioskStatus = "draft" | "active" | "maintenance" | "disabled";

export type KioskReaderType = "m2" | "tap_to_pay";

export type KioskReaderStatus = "paired" | "unpaired" | "offline";

// NfcTarget — JSONB discriminated union on kiosk.nfc_position_override
// (mirrors device_model.NfcTarget). Two concrete shapes per the spec
// (kind=on_screen | bezel). Kept loose here because the page surfaces
// the field via the detail page (γ.2), not the list.
export type NfcTarget =
  | { kind: "on_screen"; x: number; y: number; radius: number; label?: string }
  | { kind: "bezel"; edge: "top" | "right" | "bottom" | "left"; position: number; label?: string };

// ── KioskSettings (settings_json embedded on Kiosk) ────────────────
// Mirrors v1 types/index.ts:525-546 + the inline shape at
// src/api/app/services/kiosk_service.py. CHG-1198 added the kiosk_mode
// block for tap-to-pay-paired kiosks.

export interface KioskSettings {
  skip_idle_screen?: boolean;
  idle_timeout_seconds?: number;
  allowed_designation_ids?: string[];
  allowed_campaign_ids?: string[];
  // CHG-0361 — propagated to the kiosk via heartbeat response.
  capture_payment_method?: boolean;
  max_donations_per_session?: number;
  // CHG-1198 / ITER-3317 — soft-paired mobile kiosk-mode config.
  // Surfaced in γ.2 ConfigTab when reader_type=tap_to_pay; ignored by
  // the hardware kiosk app. Spec: tier-05-experience/kiosk-mode-settings.form.yaml
  kiosk_mode?: {
    preset_amounts?: number[];           // minor units (cents)
    default_designation_id?: string;
    receipt_prompt_enabled?: boolean;
    receipt_prompt_copy?: string;
    exit_pin_hash?: string;              // bcrypt/scrypt; never plaintext
  };
}

// Compact rollup embedded on Kiosk + KioskListResponse rows. Every
// field nullable because a kiosk that never reported has null.
export interface LatestHeartbeatSummary {
  app_version?: string | null;
  battery_percent?: number | null;
  network_status?: "online" | "degraded" | "offline" | null;
  reader_connected?: boolean | null;
  offline_queue_depth?: number | null;
  reported_at?: string | null;          // ISO 8601
}

// ── Kiosk entity ───────────────────────────────────────────────────
// Mirrors KioskRead in specs/tier-04-interface/kiosk-management.api.yaml
// (lines 73-93) + the deployed Pydantic at src/api/app/schemas/kiosk.py.

export interface Kiosk {
  id: string;
  organization_id: string;
  name: string;
  location: string;
  device_identifier?: string | null;
  reader_label?: string | null;
  reader_serial_number?: string | null;
  reader_status: KioskReaderStatus;
  reader_type: KioskReaderType;
  device_model_id?: string | null;
  nfc_position_override?: NfcTarget | null;
  status: KioskStatus;
  idle_timeout_seconds: number;
  brand_message?: string | null;
  settings_json?: KioskSettings | null;
  last_seen_at?: string | null;        // ISO 8601
  created_at: string;
  latest_heartbeat?: LatestHeartbeatSummary | null;
}

// POST /kiosks payload — server assigns organization_id from the
// tenant + id/created_at; reader_serial_number + last_seen_at set
// only after pair-terminal / first heartbeat.
export interface KioskCreateRequest {
  name: string;
  location: string;
  device_identifier?: string | null;
  reader_label?: string | null;
  reader_status?: KioskReaderStatus;        // default "unpaired"
  reader_type?: KioskReaderType;            // default "m2"
  device_model_id?: string | null;
  nfc_position_override?: NfcTarget | null;
  status?: KioskStatus;                     // default "draft"
  idle_timeout_seconds?: number;            // default 45
  brand_message?: string | null;
}

// PATCH /kiosks/{kioskId} payload — partial update; every field
// optional. Adds settings_json over create body.
export interface KioskUpdateRequest {
  name?: string | null;
  location?: string | null;
  reader_label?: string | null;
  reader_status?: KioskReaderStatus | null;
  reader_type?: KioskReaderType | null;
  device_model_id?: string | null;
  nfc_position_override?: NfcTarget | null;
  status?: KioskStatus | null;
  idle_timeout_seconds?: number | null;
  brand_message?: string | null;
  settings_json?: KioskSettings | null;
}

// BE-native paged envelope ({data, meta}) — kept for callers that
// need the raw page/per_page/pages metadata; the listKiosks() helper
// below normalizes to the v2 {items, total} shape that ListPage
// expects.
export interface KioskPagedEnvelope {
  data: Kiosk[];
  meta: {
    page: number;        // 1-indexed
    per_page: number;
    total: number;
    pages: number;
  };
}

// GET /kiosks/stats — status-bucket counts.
export interface KioskStats {
  total: number;
  active: number;
  draft: number;
  maintenance: number;
  disabled: number;
}

// GET /kiosks/summary — main-dashboard compact health overview.
export interface KioskDashboardSummary {
  total_kiosks: number;
  online_count: number;
  offline_count: number;
  today_donations_count: number;
  today_donations_minor_units: number;
  any_reader_disconnected: boolean;
}

// POST /kiosks/{kioskId}/pair-terminal — bind a Stripe reader.
export interface PairTerminalRequest {
  reader_label: string;
  reader_serial_number?: string | null;
}

// POST /kiosks/{kioskId}/pairing-code response — SHORT shape; the
// long-form is exchanged by the device later via the public
// /kiosks/pair endpoint (outside this admin surface).
export interface PairingCodeResponse {
  code: string;            // 6-digit zero-padded
  expires_in: number;      // seconds (10-min TTL = 600)
}

// GET /kiosks/{kioskId}/vocabulary — resolved org vocabulary
// (CHG-0149 P1). Kept loose; consumed verbatim by the device.
export interface KioskVocabulary {
  terms: Record<string, unknown>;
  ui: Record<string, unknown>;
  phrases: Record<string, unknown>;
  vocab_hash: string;
}

// POST /kiosks/create-recurring (CHG-1062 bi-channel identity:
// donor_email + donor_phone are co-equal; at least one required).
export interface KioskRecurringCreateRequest {
  designation_id: string;
  amount_minor_units: number;
  currency?: string;                  // default "USD"
  frequency: "weekly" | "monthly" | "annually";
  donor_email?: string | null;
  donor_name?: string | null;
  donor_phone?: string | null;
  intent_key?: string | null;
}

export interface KioskRecurringResponse {
  recurring_donation_id: string;
  subscription_id: string;
  client_secret: string;
}

// ── Heartbeat ──────────────────────────────────────────────────────
// Stored heartbeat row (GET /kiosks/{kioskId}/heartbeats). Newer
// CHG-0020 P9 fields (crash_count, uptime_seconds) are optional —
// older kiosk builds omit them.
export interface KioskHeartbeat {
  id: string;
  kiosk_id: string;
  reported_at: string;
  app_version: string;
  battery_percent: number | null;
  network_status: "online" | "degraded" | "offline";
  reader_connected: boolean;
  reader_disconnected_since: string | null;
  offline_queue_depth: number;
  crash_count?: number | null;
  uptime_seconds?: number | null;
}

// POST /kiosks/{kioskId}/heartbeat payload — usually called by the
// kiosk app, not the dashboard. Kept here for completeness.
export interface KioskHeartbeatCreate {
  reported_at: string;
  app_version: string;
  battery_percent?: number | null;
  network_status?: "online" | "degraded" | "offline";
  reader_connected?: boolean;
  reader_disconnected_since?: string | null;
  offline_queue_depth?: number;
  crash_count?: number | null;
  uptime_seconds?: number | null;
}

// POST /kiosks/{kioskId}/heartbeat response — persisted heartbeat
// plus the three change-detection extras the router injects
// (config_hash, vocab_hash, settings). CHG-0149 P1.
export interface KioskHeartbeatResponse extends KioskHeartbeat {
  config_hash: string;        // 12-char MD5 prefix of settings_json
  vocab_hash: string;         // 12-char MD5 prefix of resolved vocab
  settings: KioskSettings;
}

// ── DeviceModel ────────────────────────────────────────────────────
// Mirrors v1 types/index.ts:584-599. /device-models is served by a
// separate router but the kiosk admin form reads it to populate the
// "device model" dropdown when reader_type=tap_to_pay. Kept under
// kiosk-management because the surface that consumes it is.
export interface DeviceModel {
  id: string;
  slug: string;
  display_name: string;
  manufacturer?: string | null;
  form_factor: "tablet" | "phone" | "kiosk_device";
  nfc_support: "none" | "back" | "front" | "bezel";
  tap_to_pay_eligible: boolean;
  nfc_position_default?: NfcTarget | null;
  screen_aspect?: string | null;
  icon_url?: string | null;
  notes?: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// ── Donation read shape (GET /kiosks/{kioskId}/donations) ──────────
// Free-form dict per the spec's dict-shape convention; declared as a
// concrete interface here to keep the FE typed.
export interface KioskDonation {
  id: string;
  amount_minor_units: number;
  currency: string;
  status: string;
  designation_id: string;
  donor_name?: string | null;
  donor_email?: string | null;
  anonymous?: boolean;
  payment_method?: string | null;
  paid_at?: string | null;
  created_at: string;
}

export interface KioskDonationList {
  data: KioskDonation[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

// GET /kiosks/{kioskId}/fund-stats — today/month/all-time totals in
// the org's default currency.
export interface KioskFundStats {
  today_minor_units: number;
  today_count: number;
  month_minor_units: number;
  month_count: number;
  all_time_minor_units: number;
  all_time_count: number;
}

// GET /kiosks/{kioskId}/donation-chart — bar-chart aggregation.
// Free-form dict per the spec dict-shape convention.
export interface KioskDonationChart {
  data: Array<Record<string, unknown>>;
  categories: string[];
}

// POST /kiosks/{kioskId}/offline-queue/sync — response after
// replaying batched offline donations. Free-form dict per the spec.
export interface KioskOfflineQueueSyncResponse {
  accepted: Array<Record<string, unknown>>;
  rejected: Array<Record<string, unknown>>;
  total: number;
  accepted_count: number;
  rejected_count: number;
}

// ── List / fetch params ────────────────────────────────────────────

export interface ListKiosksParams {
  page?: number;        // 0-indexed for ListPage; translated to 1-indexed for BE
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
  // CHG-1198 — type filter chip drives this field; sent only when set.
  reader_type?: KioskReaderType;
  // Generic filters bag forwarded from ListPage filter chips; merges
  // into the per-field values above when the same key is set in both.
  filters?: Record<string, string | number | boolean | undefined>;
}

// ── Client functions ───────────────────────────────────────────────

export async function listKiosks(
  params: ListKiosksParams = {},
): Promise<{ items: Kiosk[]; total: number }> {
  const filters = params.filters ?? {};
  // ListPage hands us 0-indexed page; the BE wants 1-indexed.
  const pageZeroBased = params.page ?? 0;
  const pageSize = params.pageSize ?? 50;
  const r = await apiClient().get<KioskPagedEnvelope>("/kiosks", {
    params: {
      page: pageZeroBased + 1,
      per_page: pageSize,
      // The BE search param is a name substring (case-insensitive).
      search: params.search,
      // status + reader_type both accept the filter-chip bag values.
      // Per the donations.ts precedent, filters bag wins over explicit
      // params so the chip-driven UI stays the source of truth.
      status: filters.status ?? params.status,
      reader_type: filters.reader_type ?? params.reader_type,
    },
  });
  return { items: r.data.data, total: r.data.meta.total };
}

export async function getKiosk(id: string): Promise<Kiosk> {
  const r = await apiClient().get<Kiosk>(`/kiosks/${id}`);
  return r.data;
}

export async function createKiosk(data: KioskCreateRequest): Promise<Kiosk> {
  const r = await apiClient().post<Kiosk>("/kiosks", data);
  return r.data;
}

export async function updateKiosk(
  id: string,
  data: KioskUpdateRequest,
): Promise<Kiosk> {
  const r = await apiClient().patch<Kiosk>(`/kiosks/${id}`, data);
  return r.data;
}

export async function deleteKiosk(id: string): Promise<void> {
  await apiClient().delete(`/kiosks/${id}`);
}

export async function getKioskStats(): Promise<KioskStats> {
  const r = await apiClient().get<KioskStats>("/kiosks/stats");
  return r.data;
}

export async function getKioskSummary(): Promise<KioskDashboardSummary> {
  const r = await apiClient().get<KioskDashboardSummary>("/kiosks/summary");
  return r.data;
}

export async function pairTerminal(
  id: string,
  data: PairTerminalRequest,
): Promise<Kiosk> {
  const r = await apiClient().post<Kiosk>(`/kiosks/${id}/pair-terminal`, data);
  return r.data;
}

export async function generatePairingCode(
  id: string,
): Promise<PairingCodeResponse> {
  const r = await apiClient().post<PairingCodeResponse>(
    `/kiosks/${id}/pairing-code`,
  );
  return r.data;
}

export async function getKioskVocabulary(id: string): Promise<KioskVocabulary> {
  const r = await apiClient().get<KioskVocabulary>(`/kiosks/${id}/vocabulary`);
  return r.data;
}

export async function getKioskDonationChart(
  id: string,
  days = 30,
): Promise<KioskDonationChart> {
  const r = await apiClient().get<KioskDonationChart>(
    `/kiosks/${id}/donation-chart`,
    { params: { days } },
  );
  return r.data;
}

export async function getKioskDonations(
  id: string,
  params: { page?: number; per_page?: number } = {},
): Promise<KioskDonationList> {
  const r = await apiClient().get<KioskDonationList>(`/kiosks/${id}/donations`, {
    params: {
      page: (params.page ?? 0) + 1,
      per_page: params.per_page ?? 25,
    },
  });
  return r.data;
}

export async function getKioskFundStats(id: string): Promise<KioskFundStats> {
  const r = await apiClient().get<KioskFundStats>(`/kiosks/${id}/fund-stats`);
  return r.data;
}

export async function listKioskHeartbeats(
  id: string,
  limit = 50,
): Promise<KioskHeartbeat[]> {
  const r = await apiClient().get<KioskHeartbeat[]>(`/kiosks/${id}/heartbeats`, {
    params: { limit },
  });
  return r.data;
}

// POST /kiosks/{kioskId}/heartbeat — rarely called from the dashboard
// (the kiosk app is the usual caller). Exposed here so admin tooling
// + e2e tests can synthesize heartbeats.
export async function recordKioskHeartbeat(
  id: string,
  data: KioskHeartbeatCreate,
): Promise<KioskHeartbeatResponse> {
  const r = await apiClient().post<KioskHeartbeatResponse>(
    `/kiosks/${id}/heartbeat`,
    data,
  );
  return r.data;
}

export async function createKioskRecurring(
  data: KioskRecurringCreateRequest,
): Promise<KioskRecurringResponse> {
  const r = await apiClient().post<KioskRecurringResponse>(
    "/kiosks/create-recurring",
    data,
  );
  return r.data;
}

export async function syncKioskOfflineQueue(
  id: string,
  data: { items: Array<Record<string, unknown>> },
): Promise<KioskOfflineQueueSyncResponse> {
  const r = await apiClient().post<KioskOfflineQueueSyncResponse>(
    `/kiosks/${id}/offline-queue/sync`,
    data,
  );
  return r.data;
}

// Hotfix — merge CHG-2062 γ.2 short-name aliases into the canonical long-name
// export. Previously declared as two separate `export const kiosksApi = {...}`,
// which is a TS duplicate-identifier compile error: only one binding is used
// (the long-name one); short-name callsites in KioskDetail.tsx fail with
// "Property '<alias>' does not exist". Single combined export resolves it.
export const kiosksApi = {
  // Canonical γ.1 long-name surface
  listKiosks,
  getKiosk,
  createKiosk,
  updateKiosk,
  deleteKiosk,
  getKioskStats,
  getKioskSummary,
  pairTerminal,
  generatePairingCode,
  getKioskVocabulary,
  getKioskDonationChart,
  getKioskDonations,
  getKioskFundStats,
  listKioskHeartbeats,
  recordKioskHeartbeat,
  createKioskRecurring,
  syncKioskOfflineQueue,
  // CHG-2062 γ.2 short-name aliases (KioskDetail.tsx uses `kiosksApi.get(id)`,
  // `kiosksApi.update(id, data)`, etc.)
  get: getKiosk,
  update: updateKiosk,
  getDonations: getKioskDonations,
  getDonationChart: getKioskDonationChart,
  listHeartbeats: listKioskHeartbeats,
  getFundStats: getKioskFundStats,
};
